import { zodResolver } from '@hookform/resolvers/zod'
import { CircleCheckIcon } from 'lucide-react'
import { useEffect, useMemo, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import useSWR from 'swr'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiFetcher } from '@/data/fetcher'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import {
  AdminSubscriptionPlan,
  AdminSubscriptionPlanListResponse,
} from '@/types/AdminSubscription'

import {
  AdminAccountSubscriptionFormSchema,
  AdminAccountSubscriptionFormValues,
} from './schema'
import { AdminAccountSubscriptionFormFields } from './AdminAccountSubscriptionFormFields'

interface AdminAccountSubscriptionFormModalProps {
  accountId: string
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

const toDateInputValue = (date: Date): string => date.toISOString().slice(0, 10)

const buildDefaultsForPlan = (
  plan: AdminSubscriptionPlan,
  startDate = toDateInputValue(new Date()),
): AdminAccountSubscriptionFormValues => {
  const end = new Date(startDate)
  end.setDate(end.getDate() + plan.duration)

  return {
    subscriptionPlanId: plan.id,
    startDate,
    endDate: toDateInputValue(end),
    price: Number(plan.price.amount) / 100,
  }
}

export const AdminAccountSubscriptionFormModal = ({
  accountId,
  isOpen,
  onClose,
  onSuccess,
}: AdminAccountSubscriptionFormModalProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'admin.accounts.detail.subscriptions',
  })

  const initializedRef = useRef(false)

  const { data: plansResponse, error: plansError, isLoading: plansLoading } = useSWR<AdminSubscriptionPlanListResponse>(
    isOpen ? '/api/v2/admin/subscription-plans' : null,
    apiFetcher,
  )

  const plans = useMemo(
    () => plansResponse?.data ?? [],
    [plansResponse?.data],
  )

  const methods = useForm<AdminAccountSubscriptionFormValues>({
    resolver: zodResolver(AdminAccountSubscriptionFormSchema),
    defaultValues: {
      subscriptionPlanId: '',
      startDate: toDateInputValue(new Date()),
      endDate: toDateInputValue(new Date()),
      price: 0,
    },
  })

  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false
      return
    }

    if (initializedRef.current || plans.length === 0) {
      return
    }

    initializedRef.current = true
    methods.reset(buildDefaultsForPlan(plans[0]))
  }, [isOpen, plans, methods])

  const handlePlanChange = (planId: string) => {
    const plan = plans.find((item) => item.id === planId)

    if (!plan) {
      methods.setValue('subscriptionPlanId', planId)
      return
    }

    const startDate = methods.getValues('startDate') || toDateInputValue(new Date())
    methods.reset(buildDefaultsForPlan(plan, startDate))
  }

  const handleStartDateChange = (startDate: string) => {
    methods.setValue('startDate', startDate)

    const plan = plans.find(
      (item) => item.id === methods.getValues('subscriptionPlanId'),
    )

    if (!plan || !startDate) {
      return
    }

    const end = new Date(startDate)
    end.setDate(end.getDate() + plan.duration)
    methods.setValue('endDate', toDateInputValue(end))
  }

  const onSubmit = async (values: AdminAccountSubscriptionFormValues) => {
    const plan = plans.find((item) => item.id === values.subscriptionPlanId)

    if (!plan) {
      return
    }

    try {
      await API.post(`/api/v2/admin/accounts/${accountId}/subscriptions`, {
        subscriptionPlanId: values.subscriptionPlanId,
        startDate: new Date(values.startDate).toISOString(),
        endDate: new Date(values.endDate).toISOString(),
        price: {
          amount: String(Math.round(values.price * 100)),
          currency: plan.price.currency,
        },
      })

      toast.success(t('create.success'))
      onSuccess?.()
      onClose()
    } catch (error: unknown) {
      toast.error(t('create.error'), {
        description: extractErrorMessage(error),
      })
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('create.title')}</DialogTitle>
        </DialogHeader>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="w-full space-y-6"
          >
            <AdminAccountSubscriptionFormFields
              plans={plans}
              onPlanChange={handlePlanChange}
              onStartDateChange={handleStartDateChange}
            />

            {plansLoading && (
              <p className="text-muted-foreground text-sm">{t('create.loadingPlans')}</p>
            )}

            {plansError && (
              <p className="text-destructive text-sm">{t('create.plansError')}</p>
            )}

            {!plansLoading && !plansError && plans.length === 0 && (
              <p className="text-muted-foreground text-sm">{t('create.noPlans')}</p>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('form.cancel')}
              </Button>
              <Button type="submit" disabled={plans.length === 0}>
                <CircleCheckIcon className="size-5" />
                {t('form.save')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
